import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar, MapPin, Clock, ExternalLink, Sparkles, Users, Zap } from 'lucide-react';

export const Events = () => {
  // Load Eventbrite widget script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://widgets.sociablekit.com/eventbrite-events/widget.js';
    script.defer = true;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://widgets.sociablekit.com/eventbrite-events/widget.js"]');
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  }, []);

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-hr-warm-bg via-white to-hr-purple-light/20 relative" data-testid="events-hero">
        <div className="absolute top-0 right-0 w-96 h-96 bg-hr-purple/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-hr-teal/10 rounded-full blur-3xl animate-float-slow" />
        <div className="absolute top-1/3 left-1/3 w-40 h-40 bg-hr-purple/5 rounded-full blur-2xl animate-blob" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hr-purple/10 mb-6 animate-bounce-subtle">
              <Calendar className="h-4 w-4 text-hr-purple animate-pulse" />
              <span className="text-hr-purple text-sm font-medium">Upcoming Events</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-hr-text-primary animate-fade-in-up">
              HR Nest <span className="text-gradient bg-gradient-to-r from-hr-purple to-hr-teal bg-clip-text text-transparent animate-gradient">Events</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-hr-text-secondary leading-relaxed animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
              Community experiences that inspire growth and fund impact.
            </p>
          </div>
        </div>
      </section>

      {/* Eventbrite Events Section */}
      <section className="py-24 bg-white relative overflow-hidden" data-testid="events-eventbrite">
        <div className="absolute top-1/2 left-0 w-64 h-64 bg-hr-purple/5 rounded-full blur-3xl -translate-y-1/2 animate-pulse-slow" />
        <div className="absolute top-1/2 right-0 w-64 h-64 bg-hr-teal/5 rounded-full blur-3xl -translate-y-1/2 animate-pulse-slow stagger-2" />
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hr-teal/10 mb-4">
              <Sparkles className="h-4 w-4 text-hr-teal animate-pulse" />
              <span className="text-hr-teal text-sm font-medium">Live Events</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-hr-text-primary animate-fade-in-up">
              Upcoming Events
            </h2>
            <p className="mt-4 text-lg text-hr-text-secondary animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
              Join us for workshops, networking sessions, and community gatherings
            </p>
          </div>

          {/* Eventbrite Widget */}
          <Card className="border-0 shadow-2xl overflow-hidden animate-fade-in-up hover:shadow-3xl transition-all duration-500">
            <CardContent className="p-8 sm:p-12">
              <div className="sk-eventbrite-event" data-embed-id="25652749"></div>
            </CardContent>
          </Card>

          <div className="mt-8 text-center animate-fade-in-up stagger-2" style={{ opacity: 0 }}>
            <div className="flex items-center justify-center gap-3 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_mindful-coaching-10/artifacts/3biscd7s_download.png" 
                alt="Eventbrite Logo" 
                className="h-8 w-auto"
              />
              <span className="font-heading text-lg font-semibold text-hr-text-primary">
                Powered by Eventbrite
              </span>
            </div>
            <p className="text-sm text-hr-text-muted">
              All HR Nest events are available for registration on Eventbrite
            </p>
          </div>
        </div>
      </section>

      {/* Event Types Section */}
      <section className="py-24 bg-gradient-to-br from-hr-warm-bg to-white relative overflow-hidden" data-testid="events-types">
        <div className="absolute top-1/2 left-0 w-64 h-64 bg-hr-purple/5 rounded-full blur-3xl -translate-y-1/2 animate-pulse-slow" />
        <div className="absolute top-1/2 right-0 w-64 h-64 bg-hr-teal/5 rounded-full blur-3xl -translate-y-1/2 animate-pulse-slow stagger-2" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {[
              { icon: Calendar, label: 'Workshops', color: 'purple' },
              { icon: MapPin, label: 'Virtual & In-Person', color: 'teal' },
              { icon: Clock, label: 'Networking Sessions', color: 'purple' },
            ].map((item, index) => (
              <div 
                key={index}
                className="flex items-center gap-2 text-hr-text-muted px-4 py-2 rounded-full bg-white shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s`, opacity: 0 }}
              >
                <item.icon className={`h-5 w-5 ${item.color === 'purple' ? 'text-hr-purple' : 'text-hr-teal'} animate-pulse-subtle`} />
                <span>{item.label}</span>
              </div>
            ))}
          </div>

          <div className="space-y-4 animate-fade-in-up stagger-3" style={{ opacity: 0 }}>
            <p className="text-hr-text-secondary">
              Want to be notified when new events are announced?
            </p>
            <Link to="/contact" data-testid="events-notify-btn">
              <Button className="group bg-hr-purple hover:bg-hr-purple-dark text-white rounded-full px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:scale-105">
                Contact Us to Get Notified
                <ExternalLink className="ml-2 h-5 w-5 group-hover:rotate-12 transition-transform duration-300" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-24 bg-white relative overflow-hidden" data-testid="events-expect">
        <div className="absolute top-20 right-20 w-40 h-40 bg-hr-purple/10 rounded-full blur-2xl animate-float" />
        <div className="absolute bottom-20 left-20 w-60 h-60 bg-hr-teal/10 rounded-full blur-3xl animate-float-slow" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hr-teal/10 mb-4">
              <Zap className="h-4 w-4 text-hr-teal animate-pulse" />
              <span className="text-hr-teal text-sm font-medium">Preview</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-hr-text-primary animate-fade-in-up">
              What to Expect
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Calendar, title: 'Workshops & Webinars', desc: 'Interactive learning sessions on career development, professional skills, and personal growth.', color: 'purple' },
              { icon: MapPin, title: 'Community Meetups', desc: 'Connect with fellow HR Nest members in your area through local networking events.', color: 'teal' },
              { icon: Sparkles, title: 'Special Events', desc: 'Exclusive gatherings, guest speakers, and special programs for our community members.', color: 'purple' },
            ].map((item, index) => (
              <Card 
                key={index}
                className="group border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 text-center overflow-hidden animate-fade-in-up"
                style={{ animationDelay: `${index * 0.15}s`, opacity: 0 }}
              >
                <CardContent className="p-8 relative">
                  {/* Hover gradient */}
                  <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500 ${
                    item.color === 'purple' ? 'bg-gradient-to-br from-hr-purple/5 to-transparent' : 'bg-gradient-to-br from-hr-teal/5 to-transparent'
                  }`} />
                  
                  <div className={`relative w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-6 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 shadow-lg ${
                    item.color === 'purple' ? 'bg-hr-purple-light group-hover:bg-hr-purple' : 'bg-hr-teal-light group-hover:bg-hr-teal'
                  }`}>
                    <item.icon className={`h-8 w-8 transition-colors duration-500 ${
                      item.color === 'purple' ? 'text-hr-purple group-hover:text-white' : 'text-hr-teal group-hover:text-white'
                    }`} />
                  </div>
                  <h3 className="relative font-heading text-xl font-semibold text-hr-text-primary mb-3 group-hover:text-hr-purple transition-colors duration-300">
                    {item.title}
                  </h3>
                  <p className="relative text-hr-text-secondary text-sm">
                    {item.desc}
                  </p>
                  
                  {/* Bottom accent */}
                  <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-1 w-0 group-hover:w-1/2 transition-all duration-500 rounded-full ${
                    item.color === 'purple' ? 'bg-hr-purple' : 'bg-hr-teal'
                  }`} />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Events;
